import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Button,
  CircularProgress,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { getPurchaseOrderById } from "../../Services/PurchaseOrderService";
import dayjs from "dayjs";
import PrintIcon from "@mui/icons-material/Print";
import Swal from "sweetalert2";
import Footer from "../../Footer/Footer";
import { generatePurchaseOrderPDF, getCurrentFinancialYear, getStateCode } from "../../Services/CommonService";
import { getOrganizationDetails } from "../../Services/orgService";
import { getUsersByCustomerId } from "../../Services/userService";
import { ICustomer } from "../../Interfaces/IInvoice";
const ViewPODetails: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const poNumber = queryParams.get("poId");
  const orgId = localStorage.getItem("orgId") || "";
  const financialYear = getCurrentFinancialYear() ?? "";
   const [po, setPo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [orgDetails, setOrgDetails] = useState<any>(null);
  const [vendors, setVendors] = useState<ICustomer[]>([]);
  const [sellerStateCode, setSellerStateCode] = useState<string>("");
  const [vendorStateCode, setVendorStateCode] = useState<string>("");
  
  useEffect(() => {
    const fetchPODetails = async () => {
      if (!poNumber) return;
      try {
        setLoading(true);
        const res = await getPurchaseOrderById({ poNumber, orgId, financialYear });
        setPo(res);
      } catch (error: any) {
        Swal.fire("Error", error.message || "Failed to fetch PO details", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchPODetails();
  }, [poNumber, orgId, financialYear]);
  
  const loadOrgDetails = async () => {
        try {
    
          const orgData = await getOrganizationDetails(orgId);
          setOrgDetails(orgData);
        }
        catch (error) {
          console.error("Error loading Org details:", error);
        }
      }

  const loadVendors = async () => {
    try {
      const vendorData = await getUsersByCustomerId(orgId,po.vendorId);
      const vendorList = vendorData?.map((vendor: any) => ({
        id: vendor.Id,
        name:
          vendor?.customerName ??
          (vendor?.companyName ||
            [vendor?.firstName, vendor?.lastName].filter(Boolean).join(" ")),
        email: vendor?.email || "",
        GSTIN: vendor?.GSTIN || "",
        phone: vendor?.phone || "",
        street: vendor?.address?.street || "",
        city: vendor?.address?.city || "",
        state: vendor?.address?.state || "",
        zipCode: vendor?.address?.zipCode || "",
        country: vendor?.address?.country || "",
        companyName: vendor?.companyName || "",
      }));
      setVendors(vendorList);
    } catch (error) {
      console.error("Error loading Vendors:", error);
    }
  };

  useEffect(()=>{
  loadOrgDetails();
  loadVendors();
  },[po?.vendorId]);
  
  useEffect(() => {
     if (orgDetails?.address?.[0]?.state) {
       setSellerStateCode(getStateCode(orgDetails.address[0].state));
     }
   }, [orgDetails]);
 
   useEffect(() => {
     if (vendors[0]?.state) {
       setVendorStateCode(getStateCode(vendors[0].state));
     }
   }, [vendors[0]?.id]); 

 const items = po?.items || [];

   const totalSummary = items.reduce(
    (acc: { subTotal: number; discountTotal: number; taxableTotal: number; },
     item: any ) => {
    const qty = Number(item.quantity) || 0;
    const price = Number(item.unitPrice) || 0;
    const discountPct = Number(item.discount) || 0;
    const subtotal = qty * price;
    const discountAmt = (subtotal * discountPct) / 100;
    const taxable = subtotal - discountAmt;
      return {
        subTotal: acc.subTotal + subtotal,
        discountTotal: acc.discountTotal + discountAmt,
        taxableTotal: acc.taxableTotal + taxable,
      };
    },
    { subTotal: 0, discountTotal: 0, taxableTotal: 0 }
  );

 
  const isIntraState = sellerStateCode === vendorStateCode;

  let totalCGST: number = 0;
  let totalSGST: number = 0;
  let totalIGST: number = 0;

  items?.forEach((item: any) => {
   const qty = Number(item.quantity) || 0;
  const price = Number(item.unitPrice) || 0;
  const discountPct = Number(item.discount) || 0;
  const taxRate = Number(item.taxRate) || 0;

  const subtotal = qty * price;
  const discountAmt = (subtotal * discountPct) / 100;
  const taxable = subtotal - discountAmt;
    if (isIntraState) {
      // ➤ Intra-state → CGST + SGST
      const halfTaxRate = item.taxRate / 2;

      totalCGST += (taxable * halfTaxRate) / 100;
      totalSGST += (taxable * halfTaxRate) / 100;
    } else {
      // ➤ Inter-state → IGST
      totalIGST += (taxable * item.taxRate) / 100;
    }
  });
  const grandTotal =
    totalSummary.taxableTotal
    + totalCGST
    + totalSGST
    + totalIGST;

  const gstBreakup = {
    subTotal: totalSummary.subTotal || 0,
    discount: totalSummary.discountTotal || 0,
    taxable: totalSummary.taxableTotal || 0,
    cgst: totalCGST || 0,
    sgst: totalSGST || 0,
    igst: totalIGST || 0,
    grandTotal,
  }; 
  const handlePrint = () => {
  if (!po || !vendors[0] || !orgDetails) return;
  const normalizedPO = {
    ...po,
    items: po.items.map((item: any) => ({
      ...item,
      rate: item.rate ?? item.unitPrice ?? 0,
      taxPercent: item.taxPercent ?? item.taxRate ?? 0,
    })),
  };
  generatePurchaseOrderPDF(
    orgDetails || {},
    normalizedPO,                      
    vendors[0],
    gstBreakup,           
    isIntraState        
  );
};

  if (loading)
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="80vh"
      >
        <CircularProgress />
      </Box>
    );

  if (!po)
    return (
      <Typography variant="h6" textAlign="center" mt={5}>
        No Purchase Order found.
      </Typography>
    );

  return (
    <>
     <div className="page-location">
        <span>Purchase &gt; View PO Details</span>
      </div>
      <Container maxWidth="md" sx={{ my: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight="bold">
            Purchase Order Details
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
          >
            Print
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* PO Info */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            PO Number: {po.poNumber}
          </Typography>
          <Typography>Status: {po.status}</Typography>
          <Typography>
            PO Date: {dayjs(po.poDate).format("DD MMM YYYY")}
          </Typography>
          <Typography>Vendor: {po.vendorName}</Typography>
          <Typography>Grand Total: ₹{po.grandTotal?.toLocaleString("en-IN")}</Typography>
        </Paper>

        {/* Items Table */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" mb={2}>
            Item Details
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Item Name</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell align="right">Rate (₹)</TableCell>
                <TableCell align="right">Amount (₹)</TableCell>
                <TableCell align="right">Disc (%)</TableCell>
                <TableCell align="right">Disc Amt (₹)</TableCell>
                <TableCell align="right">GST (%)</TableCell>
                <TableCell align="right">GST Amt (₹)</TableCell>
                <TableCell align="right">Total (₹)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
        {po.items?.map((item: any, index: number) => {
            // safe numeric values
            const qty = Number(item.quantity) || 0;
            const unitPrice = Number(item.unitPrice) || 0;
            const discountPct = Number(item.discount) || 0;   // percent
            const taxRate = Number(item.taxRate) || 0;        // percent

            // calculations
            const gross = qty * unitPrice;
            const discountAmt = (gross * discountPct) / 100;
            const taxableValue = gross - discountAmt;
            const taxAmt = (taxableValue * taxRate) / 100;
            const total = taxableValue + taxAmt;

            // formatted strings (Indian formatting)
            const fmt = (v: number) => v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

            return (
            <TableRow key={item.id || index}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{item.itemName || "-"}</TableCell>
                <TableCell align="right">{qty}</TableCell>
                <TableCell align="right">{fmt(unitPrice)}</TableCell>
                <TableCell align="right">{fmt(gross)}</TableCell>
                <TableCell align="right">{discountPct}%</TableCell>
                <TableCell align="right">{fmt(discountAmt)}</TableCell>
                <TableCell align="right">{taxRate}%</TableCell>
                <TableCell align="right">{fmt(taxAmt)}</TableCell>
                <TableCell align="right">{fmt(total)}</TableCell>
            </TableRow>
            );
        })}
        </TableBody>
          </Table>
        </Paper>

        <Box sx={{ mt: 6 }}>
          <Footer />
        </Box>
      </Container>
    </>
  );
};

export default ViewPODetails;
